import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSubscriptionWithReconnect } from './useSubscriptionWithReconnect';
import { fetchSchedules, fetchScheduleRuns } from '../store/agentScheduleSlice';
import type { AppDispatch, RootState } from '../store';

// Subscribe to new AgentScheduleRun creation (IAM-created records)
// We use a custom subscription since codegen didn't generate one for the IAM auth pattern
const onCreateAgentScheduleRun = /* GraphQL */ `
  subscription OnCreateAgentScheduleRun {
    onCreateAgentScheduleRun {
      id
      scheduleId
      status
      error
      createdAt
    }
  }
`;

const onUpdateAgentScheduleRun = /* GraphQL */ `
  subscription OnUpdateAgentScheduleRun {
    onUpdateAgentScheduleRun {
      id
      scheduleId
      status
      error
      createdAt
    }
  }
`;

interface RunSubscriptionData {
  onCreateAgentScheduleRun?: {
    id: string;
    scheduleId: string;
    status: string;
    createdAt: string;
  } | null;
}

interface RunUpdateSubscriptionData {
  onUpdateAgentScheduleRun?: {
    id: string;
    scheduleId: string;
    status: string;
    createdAt: string;
  } | null;
}

/**
 * Hook that subscribes to AgentScheduleRun creation/updates and refreshes schedule data.
 * When a new run is created or a run completes, it refetches the runs for that schedule.
 */
export function useScheduleSubscription() {
  const dispatch = useDispatch<AppDispatch>();
  const schedules = useSelector((state: RootState) => state.agentSchedule.schedules);
  const schedulesRef = useRef(schedules);

  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);

  const handleNewRun = useCallback((data: RunSubscriptionData) => {
    const run = data?.onCreateAgentScheduleRun;
    if (run?.scheduleId) {
      dispatch(fetchScheduleRuns({ scheduleId: run.scheduleId, limit: 20 }));
      dispatch(fetchSchedules());
    }
  }, [dispatch]);

  const handleRunUpdate = useCallback((data: RunUpdateSubscriptionData) => {
    const run = data?.onUpdateAgentScheduleRun;
    if (run?.scheduleId) {
      // Run completed/failed — refetch to get conversations with AI responses
      dispatch(fetchScheduleRuns({ scheduleId: run.scheduleId, limit: 20 }));
      dispatch(fetchSchedules());
    }
  }, [dispatch]);

  useSubscriptionWithReconnect<RunSubscriptionData>({
    query: onCreateAgentScheduleRun,
    onData: handleNewRun,
  });

  useSubscriptionWithReconnect<RunUpdateSubscriptionData>({
    query: onUpdateAgentScheduleRun,
    onData: handleRunUpdate,
  });
}
